import React from 'react'
import { ChevronDown, Check, Loader2, Search, UserCheck } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import AuthContext from '../../providers/AuthProvider'
import ClientContext from '../../providers/CurrentClientProvider'
import ClientService from '../../services/ClientService'
import {
  buildLegacyClientDashboardPath,
  buildV3ClientDashboardPath,
  getClientDashboardV3Enabled,
} from '../../services/ClientDashboardV3'
import { cn } from '../../lib/utils'

function filterClients(clients, keyword) {
  const normalizedKeyword = String(keyword || '').trim().toLowerCase()

  if (!normalizedKeyword) {
    return clients
  }

  return clients.filter((client) => {
    const name = String(client?.Name || '').toLowerCase()
    const code = String(client?.ClientCode || '').toLowerCase()
    return Boolean(code) && (name.includes(normalizedKeyword) || code.includes(normalizedKeyword))
  })
}

export default function V3ClientSelect({
  className,
  placeholder = 'Select a client',
  unselectNavigatePath = '/v3/home',
}) {
  const [open, setOpen] = React.useState(false)
  const [clients, setClients] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [loaded, setLoaded] = React.useState(false)
  const [keyword, setKeyword] = React.useState('')
  const [error, setError] = React.useState('')
  const dropdownRef = React.useRef(null)
  const navigate = useNavigate()
  const location = useLocation()
  const auth = AuthContext.useAuth()
  const clientContext = ClientContext.useClient()
  const currentClient = clientContext?.client || {}

  const visibleClients = React.useMemo(() => filterClients(clients, keyword), [clients, keyword])

  React.useEffect(() => {
    if (!open) {
      return undefined
    }

    function handlePointerDown(event) {
      if (!dropdownRef.current?.contains(event.target)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [open])

  const loadClients = React.useCallback(async () => {
    if (loaded || loading || !auth?.user) {
      return
    }

    setLoading(true)
    setError('')

    try {
      const response = await ClientService.searchClients()
      setClients(Array.isArray(response) ? response : [])
      setLoaded(true)
    } catch (loadError) {
      console.error('Failed to load clients for v3 selector:', loadError)
      setError('Unable to load clients right now.')
    } finally {
      setLoading(false)
    }
  }, [auth?.user, loaded, loading])

  const toggleOpen = React.useCallback(() => {
    const nextOpen = !open
    setOpen(nextOpen)

    if (nextOpen) {
      loadClients()
    }
  }, [loadClients, open])

  const chooseClient = React.useCallback((client) => {
    setOpen(false)
    clientContext?.setClient?.(client)

    const shouldUseV3 = getClientDashboardV3Enabled(client, auth?.user)
    const dashboardPath = shouldUseV3
      ? buildV3ClientDashboardPath(client?.ClientCode)
      : buildLegacyClientDashboardPath(client?.ClientCode)

    const currentPath = window.location.pathname
    if (currentPath.includes('/v3/client/')) {
      if (shouldUseV3) {
        window.location.href = currentPath.replace(/\/v3\/client\/[^/]+/, `/v3/client/${client.ClientCode}`)
      } else {
        window.location.href = dashboardPath
      }
      return
    }

    if (currentPath.includes('/client/') || currentPath.includes('/clients/')) {
      if (shouldUseV3) {
        window.location.href = dashboardPath
        return
      }

      window.location.href = currentPath.replace(/\/(client|clients)\/[^/]+/, `/$1/${client.ClientCode}`)
      return
    }

    navigate(dashboardPath)
  }, [auth?.user, clientContext, navigate])

  const unselectClient = React.useCallback(() => {
    clientContext?.setClient?.({})
    setOpen(false)

    if (location.pathname.startsWith('/v3/')) {
      navigate(unselectNavigatePath)
      return
    }

    navigate('/')
  }, [clientContext, location.pathname, navigate, unselectNavigatePath])

  const hasClient = Boolean(currentClient?.ClientCode)
  const selectedClientId = currentClient?.Id
  const currentLabel = hasClient
    ? `${currentClient.ClientCode}${currentClient.Name ? ` · ${currentClient.Name}` : ''}`
    : placeholder

  return (
    <div ref={dropdownRef} className={cn('relative', className)}>
      <button
        type="button"
        onClick={toggleOpen}
        className={cn(
          'inline-flex h-9 w-full items-center gap-2 rounded-lg border border-gray-300 bg-white px-3.5 py-2.5 text-left shadow-sm transition-colors hover:border-gray-400 cursor-pointer',
          open && 'border-gray-400 ring-2 ring-purple-100',
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <UserCheck className="h-4 w-4 shrink-0 text-gray-500" />
        <span className="flex-1 truncate text-sm font-bold text-[#101828]">{currentLabel}</span>
        <ChevronDown className={cn('h-4 w-4 shrink-0 text-gray-500 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute left-0 z-50 mt-2 w-[320px] max-w-[calc(100vw-32px)] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
          <div className="border-b border-gray-100 p-4">
            <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
              <Search className="h-4 w-4 text-gray-400" />
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="Search clients"
                className="w-full border-0 bg-transparent text-sm text-gray-700 outline-none placeholder:text-gray-400"
              />
            </div>

            <div className="mt-3 flex items-center justify-between gap-3">
              <div className="text-xs text-gray-500">
                {hasClient ? 'Switch your active client context.' : 'Select a client to open its dashboard.'}
              </div>
              {hasClient && (
                <button
                  type="button"
                  onClick={unselectClient}
                  className="text-xs font-semibold text-purple-700 hover:text-purple-800"
                >
                  Unselect
                </button>
              )}
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto p-2">
            {loading && (
              <div className="flex items-center justify-center gap-2 px-3 py-8 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading clients…
              </div>
            )}

            {!loading && error && (
              <div className="rounded-xl bg-red-50 px-3 py-3 text-sm text-red-700">{error}</div>
            )}

            {!loading && !error && visibleClients.length === 0 && (
              <div className="px-3 py-8 text-center text-sm text-gray-500">No clients match your search.</div>
            )}

            {!loading && !error && visibleClients.map((client) => {
              const isSelected = selectedClientId === client?.Id
              return (
                <button
                  key={`v3-client-${client.Id}`}
                  type="button"
                  onClick={() => chooseClient(client)}
                  className={cn(
                    'flex w-full items-start justify-between gap-3 rounded-xl px-3 py-3 text-left transition-colors',
                    isSelected ? 'bg-purple-50 text-purple-900' : 'text-gray-700 hover:bg-gray-50',
                  )}
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold">{client.ClientCode}</div>
                    <div className="mt-0.5 truncate text-xs text-gray-500">{client.Name}</div>
                  </div>
                  {isSelected && <Check className="mt-0.5 h-4 w-4 shrink-0 text-purple-700" />}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

